import type { CatalogItem, CategoryItem, FormularyItem, ManagedItemType } from "@vaulted/shared";
import { ITEM_TYPE_LABELS, totalMinutes } from "@vaulted/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { Link, Navigate, useParams } from "react-router";
import { del, errorMessage, get, patch, post, put } from "../api";
import { Alert, Badge, Button, Checkbox, Dialog, Field, Loading, PageHeader, Panel, SelectField, Tabs, TextAreaField, formatMinutes, money } from "../components/ui";
import { useSession } from "../session";
import { FormularyTab } from "./Formulary";
import { PlansTab } from "./Plans";

export interface CatalogResponse {
  categories: CategoryItem[];
  items: CatalogItem[];
  formulary: FormularyItem[];
}

export function useCatalog() {
  return useQuery({ queryKey: ["catalog"], queryFn: () => get<CatalogResponse>("/api/catalog") });
}

/** URL segment for each managed type. */
export const TYPE_PATHS: Record<ManagedItemType, string> = { service: "services", add_on: "add-ons", product: "products" };
const PATH_TYPES = Object.fromEntries(Object.entries(TYPE_PATHS).map(([t, p]) => [p, t])) as Record<string, ManagedItemType>;

const TABS = [
  { to: "/catalog/services", label: "Services" },
  { to: "/catalog/add-ons", label: "Add-ons" },
  { to: "/catalog/products", label: "Products" },
  { to: "/catalog/memberships", label: "Memberships" },
  { to: "/catalog/packages", label: "Packages" },
  { to: "/catalog/categories", label: "Categories" },
  { to: "/catalog/formulary", label: "Formulary" },
];

export function CatalogPage() {
  const { tab } = useParams();
  const catalog = useCatalog();
  if (tab !== "categories" && tab !== "formulary" && tab !== "memberships" && tab !== "packages" && !(tab && PATH_TYPES[tab])) return <Navigate to="/catalog/services" replace />;

  return (
    <div>
      <PageHeader
        title="Catalog"
        description="Everything you offer, with prices and timing. Booking, charting, and the public menu all read from here."
        action={
          <Link to="/catalog/print" className="inline-flex items-center rounded-[var(--radius-control)] border border-line bg-white px-4 py-2 text-sm font-semibold text-ink hover:border-ink-soft">
            Print menu
          </Link>
        }
      />
      <Tabs items={TABS} />
      {catalog.isLoading ? (
        <Loading />
      ) : catalog.error || !catalog.data ? (
        <Alert>{errorMessage(catalog.error)}</Alert>
      ) : tab === "categories" ? (
        <CategoriesTab categories={catalog.data.categories} />
      ) : tab === "memberships" || tab === "packages" ? (
        <PlansTab kind={tab === "memberships" ? "membership" : "package"} data={catalog.data} />
      ) : tab === "formulary" ? (
        <FormularyTab data={catalog.data} />
      ) : (
        <ItemList type={PATH_TYPES[tab!]!} data={catalog.data} />
      )}
    </div>
  );
}

function ItemList({ type, data }: { type: ManagedItemType; data: CatalogResponse }) {
  const { can } = useSession();
  const [search, setSearch] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [archived, setArchived] = useState(false);
  const labels = ITEM_TYPE_LABELS[type];
  const categoryName = (id: string | null) => data.categories.find((c) => c.id === id)?.name ?? "No category";

  const ofType = data.items.filter((i) => i.type === type);
  const archivedCount = ofType.filter((i) => i.status === "archived").length;
  const q = search.trim().toLowerCase();
  const list = ofType
    .filter((i) => (archived ? i.status === "archived" : i.status === "active"))
    .filter((i) => !categoryId || i.categoryId === categoryId)
    .filter((i) => !q || i.name.toLowerCase().includes(q) || i.description.toLowerCase().includes(q));
  // Group by category, in category order.
  const order = new Map(data.categories.map((c, i) => [c.id, i]));
  list.sort((a, b) => (order.get(a.categoryId ?? "") ?? 999) - (order.get(b.categoryId ?? "") ?? 999) || a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field label="Search" className="min-w-48 flex-1" type="search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={`Search ${labels.many.toLowerCase()}`} />
        <SelectField label="Category" className="w-48" value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
          <option value="">All categories</option>
          {data.categories.map((c) => (
            <option key={c.id} value={c.id}>
              {c.name}
            </option>
          ))}
        </SelectField>
        {can("catalog.manage") && (
          <Link
            to={`/catalog/new/${TYPE_PATHS[type]}`}
            className="inline-flex items-center rounded-[var(--radius-control)] bg-brand px-4 py-2 text-sm font-semibold text-white hover:bg-brand-deep"
          >
            Add {labels.one.toLowerCase()}
          </Link>
        )}
      </div>

      {list.length === 0 ? (
        <Alert tone="info">{q || categoryId ? "Nothing matches." : archived ? `No archived ${labels.many.toLowerCase()}.` : `No ${labels.many.toLowerCase()} yet.`}</Alert>
      ) : (
        <Panel className="overflow-x-auto">
          <table className="table-base">
            <thead>
              <tr>
                <th>Name</th>
                <th className="hidden sm:table-cell">Category</th>
                {type === "service" && <th className="hidden md:table-cell">Time</th>}
                <th className="text-right">Clinic</th>
                <th className="text-right">Mobile</th>
              </tr>
            </thead>
            <tbody>
              {list.map((i) => (
                <tr key={i.id} className="hover:bg-mist/50">
                  <td>
                    <Link to={`/catalog/item/${i.id}`} className="inline-flex items-center gap-2 font-semibold hover:text-brand">
                      {i.color && <span className="size-2.5 shrink-0 rounded-full" style={{ background: i.color }} aria-hidden />}
                      {i.name}
                    </Link>
                    <div className="flex flex-wrap gap-1.5 pt-0.5">
                      {!i.showOnPublicMenu && <Badge tone="off">Hidden from menu</Badge>}
                      {/* Neither clinic nor mobile: only on event menus. */}
                      {!i.availableClinic && !i.availableMobile ? (
                        <Badge tone="wait">Events only</Badge>
                      ) : !i.availableClinic ? (
                        <Badge tone="wait">Mobile only</Badge>
                      ) : !i.availableMobile ? (
                        <Badge tone="off">Clinic only</Badge>
                      ) : null}
                    </div>
                    <div className="text-sm text-muted sm:hidden">{categoryName(i.categoryId)}</div>
                  </td>
                  <td className="hidden text-ink-soft sm:table-cell">{categoryName(i.categoryId)}</td>
                  {type === "service" && (
                    <td className="hidden text-sm text-ink-soft md:table-cell">
                      {formatMinutes(totalMinutes(i))}
                      {i.workingMinutes > 0 && <div className="text-xs text-muted">{formatMinutes(i.startMinutes)} hands-on</div>}
                    </td>
                  )}
                  <td className="text-right tabular-nums">{i.availableClinic ? money(i.priceCents) : "—"}</td>
                  <td className="text-right tabular-nums">{i.availableMobile ? money(i.mobilePriceCents ?? i.priceCents) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </Panel>
      )}

      {archivedCount > 0 && (
        <button className="self-start text-sm font-medium text-muted hover:text-ink" onClick={() => setArchived((v) => !v)}>
          {archived ? `Show active ${labels.many.toLowerCase()}` : `Show archived (${archivedCount})`}
        </button>
      )}
    </div>
  );
}

function CategoriesTab({ categories }: { categories: CategoryItem[] }) {
  const qc = useQueryClient();
  const { can } = useSession();
  const [editing, setEditing] = useState<CategoryItem | "new" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reorder = useMutation({
    mutationFn: (ids: string[]) => put<CategoryItem[]>("/api/catalog/categories/order", { ids }),
    onMutate: () => setError(null),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["catalog"] }),
    onError: (e) => setError(errorMessage(e)),
  });
  const move = (i: number, dir: -1 | 1) => {
    const ids = categories.map((c) => c.id);
    const j = i + dir;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids);
  };
  const manage = can("catalog.manage");

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <p className="text-ink-soft">Categories group the menu on booking pages and staff screens, in this order.</p>
      {error && <Alert>{error}</Alert>}
      <Panel>
        <ul className="divide-y divide-mist">
          {categories.map((c, i) => (
            <li key={c.id} className="flex items-center gap-3 px-4 py-3">
              <div className="min-w-0 flex-1">
                <div className="font-semibold">{c.name}</div>
                <div className="truncate text-sm text-muted">
                  {c.itemCount} active item{c.itemCount === 1 ? "" : "s"}
                  {c.description && ` · ${c.description}`}
                </div>
              </div>
              {manage && (
                <div className="flex items-center gap-1">
                  <Button variant="quiet" className="px-2" disabled={i === 0 || reorder.isPending} onClick={() => move(i, -1)} aria-label={`Move ${c.name} up`}>
                    ↑
                  </Button>
                  <Button variant="quiet" className="px-2" disabled={i === categories.length - 1 || reorder.isPending} onClick={() => move(i, 1)} aria-label={`Move ${c.name} down`}>
                    ↓
                  </Button>
                  <Button variant="quiet" onClick={() => setEditing(c)}>
                    Edit
                  </Button>
                </div>
              )}
            </li>
          ))}
        </ul>
      </Panel>
      {manage && (
        <div>
          <Button onClick={() => setEditing("new")}>Add category</Button>
        </div>
      )}
      {editing && <CategoryDialog category={editing === "new" ? null : editing} onClose={() => setEditing(null)} />}
    </div>
  );
}

function CategoryDialog({ category, onClose }: { category: CategoryItem | null; onClose: () => void }) {
  const qc = useQueryClient();
  const [form, setForm] = useState({ name: category?.name ?? "", description: category?.description ?? "", onePerVisit: category?.onePerVisit ?? false });
  const done = async () => {
    await qc.invalidateQueries({ queryKey: ["catalog"] });
    onClose();
  };
  const save = useMutation({
    mutationFn: () => (category ? patch(`/api/catalog/categories/${category.id}`, form) : post("/api/catalog/categories", form)),
    onSuccess: done,
  });
  const remove = useMutation({ mutationFn: () => del(`/api/catalog/categories/${category!.id}`), onSuccess: done });
  const error = save.error ?? remove.error;

  return (
    <Dialog
      title={category ? "Edit category" : "Add category"}
      onClose={onClose}
      footer={
        <>
          {category && (
            <Button variant="danger" className="mr-auto" busy={remove.isPending} onClick={() => remove.mutate()}>
              Delete
            </Button>
          )}
          <Button variant="quiet" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" type="submit" form="category-form" busy={save.isPending}>
            Save
          </Button>
        </>
      }
    >
      <form
        id="category-form"
        className="flex flex-col gap-4"
        onSubmit={(e) => {
          e.preventDefault();
          save.mutate();
        }}
      >
        {error && <Alert>{errorMessage(error)}</Alert>}
        <Field label="Name" required maxLength={60} autoFocus value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        <TextAreaField label="Description (optional)" maxLength={300} hint="Shown under the category on the public menu." value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
        <Checkbox
          label="One per person per visit"
          description="For drips: nobody can book two services from this category on the same visit. Staff can still go ahead with a reason."
          checked={form.onePerVisit}
          onChange={(on) => setForm({ ...form, onePerVisit: on })}
        />
      </form>
    </Dialog>
  );
}
